<template>
  <div class="code-input" @click.stop="handleChangeFocus">
    <div class="inputs">
      <input
        v-for="(value, index) in code"
        :key="index"
        :ref="(el) => (refs[index] = el as HTMLInputElement | null)"
        :value="value"
        :tabindex="props.tabindex ? props.tabindex + index : undefined"
        type="text"
        inputmode="numeric"
        class="code-input-field"
        maxlength="1"
        @input="handleInput($event, index)"
        @keydown.delete="handleKeydown(index)"
      >
    </div>
    <p v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script setup lang="ts">
export interface Props {
  modelValue?: string
  tabindex?: number
  size?: 'md' | 'lg'
  length: number
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  size: 'md',
})
const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const code = computed(() =>
  Array.from(
    { length: props.length },
    (_, index) => props.modelValue.at(index) ?? '',
  ),
)

const refs: (HTMLInputElement | null)[] = []

const errorMessage = ref<string | null>(null)

function handleInput(event: Event, index: number) {
  const { value } = event.target as HTMLInputElement

  if (index === props.length || value === '') {
    return
  }

  emit('update:modelValue', props.modelValue + value)
  focusInput(index + 1)
}

function handleKeydown(index: number) {
  emit('update:modelValue', props.modelValue.slice(0, index - 1))

  if (index === 0) {
    return
  }

  setTimeout(() => {
    focusInput(index - 1)
  })
}

function handleChangeFocus() {
  const index = code.value.findIndex(val => val === '')
  focusInput(index)
}

function focusInput(index: number) {
  refs[index]?.focus()
}
</script>

<style lang="scss" scoped>
.code-input {
  display: flex;
  flex-direction: column;
  align-items: center;

  .inputs {
    display: flex;
    gap: 8px;

    input[type="number"]::-webkit-outer-spin-button,
    input[type="number"]::-webkit-inner-spin-button {
      appearance: none;
      margin: 0;
    }

    input[type="number"] {
      appearance: textfield;
    }

    .code-input-field {
      text-align: center;
      flex: 1;
      width: 100%;
      height: 38px;
      border-radius: 12px;
      background-color: var(--color-surface-placeholder);
      border: 0;
      font-weight: 700;
      transition: scale 0.3s ease-in-out;
      color: var(--color-foreground-main);
      will-change: scale;

      &:hover {
        scale: 1.03;
      }

      &:active {
        scale: 0.97;
      }

      &::placeholder {
        color: var(--color-foreground-caption);
        font-weight: 500;
      }
    }
  }

  .error-message {
    margin-top: 8px;
    color: red;
    font-size: 14px;
  }
}
</style>
